(ns hai.tasks.view
  "Stateless styled components for rendering a task's contents, decoupled
   from how a task is loaded, stored, or mutated."
  (:require
   [garden.stylesheet]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.urgency :as urgency]
   [hai.ui.components :as c]
   [hai.ui.components.datetime :as datetime]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [url.core :as url]))

;; A task's description sheet rising into view as its card presses down. Shared
;; by the home dashboard's desktop and mobile task cards.
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules detail-reveal-keyframes
  (garden.stylesheet/at-keyframes
   :task-detail-in
   ["0%"   {:opacity "0" :transform "translateY(-5px)"}]
   ["100%" {:opacity "1" :transform "none"}]))

;; Numbered, schemeless, clickable list of a task's linked URLs.
(o/defstyled task-links :div
  {:display "flex" :flex-direction "column" :gap "5px"}
  [:.task-link-row
   {:display "flex" :align-items "baseline" :gap "8px"}]
  [:.task-link-num
   {:flex "0 0 auto" :min-width "12px" :text-align "right"
    :font-size "10.5px" :color t/--link-muted}]
  [:.task-link-anchor
   {:flex "1" :min-width "0"
    :color t/--task-accent
    :font-family "ui-monospace, 'SF Mono', Menlo, monospace"
    :font-size "12px"
    :text-decoration "underline"
    :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}
   [:&:hover {:color t/--task-accent-hover}]]
  ([{:keys [links]}]
   [:<>
    (for [[idx activity] (map-indexed vector links)]
      ^{:key (or (:activity/uuid activity) (:activity/url activity))}
      [:div.task-link-row
       [:span.task-link-num (str (inc idx))]
       [:a.task-link-anchor
        {:href (:activity/url activity)
         :target "_blank"
         :rel "noopener noreferrer"
         :title (:activity/url activity)
         :on-click #(.stopPropagation %)}
        (url/url->display-label (:activity/url activity))]])]))

;; Inline deadline label: clock icon + "Due …", with the full timestamp on hover.
(o/defstyled task-deadline :span
  {:display "inline-flex" :align-items "center" :gap "5px"
   :font-size "12.5px" :font-weight "500" :line-height "1.3"
   :color t/--task-accent}
  [:svg {:flex "0 0 auto" :opacity "0.85"}]
  [:&.overdue {:color t/--card-warning-text :font-weight "600"}]
  ([{:keys [task]}]
   (when-let [label (deadline/label task)]
     [:<>
      {:class (when (deadline/overdue? task) "overdue")
       :title (deadline/tooltip task)}
      [datetime/clock-icon]
      [:span label]])))

(def ^:private urgency-icon (icons/mui-icon :outlined :bolt))

;; Segmented Now / Next / Later urgency selector shared by the web-app and
;; browser-extension composers. Radix paints the selected fill on the
;; indicator's ::before, so that (not the indicator) is what we recolour.
(o/defstyled urgency-control :div
  {:margin-top "12px"
   :display "flex" :flex-direction "column" :gap "8px"}
  [:.urgency-divider
   {:display "flex" :align-items "center" :gap "6px"}
   [:.urgency-divider-icon
    {:display "inline-flex" :align-items "center" :color t/--card-forest-70}
    [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
   [:.urgency-divider-label
    {:font-size "10px" :font-weight "700" :letter-spacing "0.06em"
     :text-transform "uppercase" :color t/--card-forest-70 :white-space "nowrap"}]
   [:.urgency-divider-rule
    {:flex "1" :border-top-width "1px" :border-top-style "dashed"
     :border-top-color t/--card-sage-light}]]
  [:.rt-SegmentedControlRoot
   {:align-self "flex-start"
    :background-color "transparent"
    :background-image "none"
    :border-width "1px" :border-style "solid" :border-color t/--card-sage-light
    :border-radius "8px"
    :box-shadow "none"}]
  [:.rt-SegmentedControlItemLabel
   {:font-family "inherit"
    :color t/--task-accent}]
  [".rt-SegmentedControlItem[data-state='off']:hover .rt-SegmentedControlItemLabel"
   {:background-color t/--card-forest-06
    :color t/--card-forest}]
  [".rt-SegmentedControlItem[data-state='on'] .rt-SegmentedControlItemLabel"
   {:color t/--card-forest :font-weight "600"}]
  [:.rt-SegmentedControlItemSeparator
   {:background-color t/--property-3}]
  [:.rt-SegmentedControlIndicator
   [:&::before {:background-color t/--property-3 :box-shadow "none"}]]
  ([{:keys [value on-change]}]
   [:<>
    [:div.urgency-divider
     [:span.urgency-divider-icon [urgency-icon]]
     [:span.urgency-divider-label "Urgency"]
     [:span.urgency-divider-rule]]
    (into [c/SegmentedControlRoot
           {:size "1"
            :value (name (or value :task.urgency/later))
            :on-value-change #(on-change (keyword "task.urgency" %))}]
          (for [{option-value :value option-label :label} urgency/options]
            ^{:key option-value}
            [c/SegmentedControlItem {:value (name option-value)} option-label]))]))
